// Pure, locale-aware date formatters (contract 04 §3.2, §5, §14). Rebuilt from the legacy
// `formatDate` / `dateRangeFormat` (`format.js:586–647`), which were defective as a contract:
//   • `formatDate` read a Pinia store INSIDE the formatter (`useWorkspaceStore()`, L597), resolved
//     the locale through the broken `_safeLocale(attributeObject)` path (analysis §2.3), and returned
//     TWO different empty renderings (`'-'` at L588 vs `''` at L595). Here it takes an explicit
//     `locale`, imports no store, and has a SINGLE `'-'` path for every empty/junk/unparseable input.
//   • `dateRangeFormat` hardcoded three locales in one function (`'en-GB'`/`'en-US'`) and emitted HTML
//     (`<br/>`). The pure bucket math becomes `formatBucketLabel(v, granularity, locale)`; the `<br/>`
//     and `W{isoWeek}` chart glyphs are presentational and belong to module 08 — they are NOT here.
//
// Preserved (contract §14 / §17 parity): the `'-'` junk sentinel list, the `T00:00:00`/` 00:00:00`
// midnight strip with UTC-day semantics (a date-only string never shifts a day by timezone), and the
// memoised `Intl.DateTimeFormat` cache. Zero Vue, zero Pinia, zero network.

import { FALLBACK_LOCALE, SENTINEL, safeLocale } from './createFormatter'
import type {
  DateBucketGranularity,
  DateInput,
  FormatDateOptions,
  FormatDateRangeOptions,
} from './types'

/** Legacy junk-sentinel list (`format.js:588`): these raw strings render as `'-'`, never a date. */
const JUNK: ReadonlySet<string> = new Set(['nan', 'Invalid Date', 'NaT', '-', 'NaN-NaN-NaN'])

/** A bare calendar day, e.g. `2026-07-14` (after any midnight marker is stripped). */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

/** Trailing midnight marker: `T00:00:00` or ` 00:00:00` (optionally with a fractional part). */
const MIDNIGHT = /[T ]00:00:00(?:\.0+)?$/

/** Runtime IANA zone, resolved once. `formatDate` uses it for date-time input (legacy `format.js:604`). */
let runtimeZone: string | undefined
function getRuntimeZone(): string {
  if (runtimeZone === undefined) {
    try {
      runtimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      runtimeZone = 'UTC'
    }
  }
  return runtimeZone
}

/** Memoised `Intl.DateTimeFormat` cache keyed by `locale|zone|shape` (legacy `_dtfCache`, L497). */
const DTF_LIMIT = 128
const dtfCache = new Map<string, Intl.DateTimeFormat>()

function getDtf(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  key: string,
): Intl.DateTimeFormat {
  const cached = dtfCache.get(key)
  if (cached) return cached

  let dtf: Intl.DateTimeFormat
  try {
    dtf = new Intl.DateTimeFormat(safeLocale(locale), options)
  } catch {
    dtf = new Intl.DateTimeFormat(FALLBACK_LOCALE, options)
  }

  dtfCache.set(key, dtf)
  if (dtfCache.size > DTF_LIMIT) {
    const oldest = dtfCache.keys().next().value
    if (oldest !== undefined) dtfCache.delete(oldest)
  }
  return dtf
}

interface ParsedDate {
  date: Date
  /** `true` when the input was a bare calendar day (no time part) — render it in UTC to avoid shift. */
  dateOnly: boolean
}

export interface ZonedDateTimeParts {
  weekday: string
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** Calendar fields in an explicit IANA zone for feature-domain cutoff calculations. */
export function getZonedDateTimeParts(
  value: DateInput,
  timeZone: string,
): ZonedDateTimeParts | null {
  if (value == null) return null
  const { date } = parseDateInput(value)
  if (Number.isNaN(date.getTime())) return null
  try {
    const formatter = getDtf(
      FALLBACK_LOCALE,
      {
        timeZone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      },
      `${FALLBACK_LOCALE}|${timeZone}|parts`,
    )
    const parts = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value]),
    )
    return {
      weekday: parts.weekday ?? '',
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
    }
  } catch {
    return null
  }
}

function parseDateInput(value: string | number | Date): ParsedDate {
  if (typeof value === 'string') {
    const stripped = value.replace(MIDNIGHT, '')
    const normalized = stripped.replace(/^(\d{4})\/(\d{2})\/(\d{2})$/, '$1-$2-$3')
    return { date: new Date(normalized), dateOnly: DATE_ONLY.test(normalized) }
  }
  if (value instanceof Date) return { date: value, dateOnly: false }
  return { date: new Date(value), dateOnly: false }
}

const NAIVE_ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/

function isoDay(value: string | number | Date, timeZone: string): string {
  const input =
    typeof value === 'string' && NAIVE_ISO_TIMESTAMP.test(value.trim()) ? `${value.trim()}Z` : value
  const parts = getZonedDateTimeParts(input, timeZone)
  if (!parts) return SENTINEL
  const pad = (part: number): string => String(part).padStart(2, '0')
  return `${String(parts.year).padStart(4, '0')}-${pad(parts.month)}-${pad(parts.day)}`
}

/**
 * Locale-aware date. Returns the `'-'` sentinel for nullish / junk (`'nan'`, `'Invalid Date'`,
 * `'NaT'`, `'-'`, `'NaN-NaN-NaN'`) / unparseable input — a single path. A date-only string renders in
 * UTC so it never shifts a day by timezone. Default shape: `year`/`month:'short'`/`day`, matching the
 * legacy formatter; pass `dateStyle` to override, `withTime` to append `hour:minute`.
 * `formatDate('2026-07-14', 'en-US')` → `"Jul 14, 2026"`; `(…, 'de-DE')` → `"14. Juli 2026"`.
 */
export function formatDate(
  value: DateInput,
  locale: string,
  options: FormatDateOptions = {},
): string {
  if (value == null) return SENTINEL
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '' || JUNK.has(trimmed)) return SENTINEL
  }

  const { date, dateOnly } = parseDateInput(value)
  if (Number.isNaN(date.getTime())) return SENTINEL

  const withTime = options.withTime ?? false
  const timeZone = options.timeZone ?? (dateOnly && !withTime ? 'UTC' : getRuntimeZone())
  if (options.iso === true) return isoDay(value, timeZone)

  const dtfOptions: Intl.DateTimeFormatOptions = { timeZone }
  if (options.dateStyle !== undefined) {
    dtfOptions.dateStyle = options.dateStyle
    if (withTime) dtfOptions.timeStyle = 'short'
  } else {
    if (options.omitYear !== true) dtfOptions.year = 'numeric'
    dtfOptions.month = 'short'
    dtfOptions.day = 'numeric'
    if (withTime) {
      dtfOptions.hour = '2-digit'
      dtfOptions.minute = '2-digit'
      dtfOptions.hour12 = options.hour12 ?? false
    }
  }

  const shape = `${options.dateStyle ?? 'ymd'}|${withTime ? 1 : 0}|${options.hour12 ? 1 : 0}|${
    options.omitYear === true ? 1 : 0
  }`
  const key = `${safeLocale(locale)}|${timeZone}|${shape}`
  return getDtf(locale, dtfOptions, key).format(date)
}

/**
 * Time of day alone — hour + minute, no calendar part. `formatDate`'s `withTime` always renders a
 * date first, so a caller that wants only a clock reading (a chat timestamp, an activity log) has
 * nothing to narrow. Same `'-'` sentinel and same memoised-`Intl` path as {@link formatDate}.
 * `formatTime('2026-08-25T17:01:00Z', 'en-US', { hour12: true })` → `"5:01 PM"` in a UTC runtime.
 */
export function formatTime(
  value: DateInput,
  locale: string,
  options: FormatDateOptions = {},
): string {
  if (value == null) return SENTINEL
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '' || JUNK.has(trimmed)) return SENTINEL
  }

  const { date } = parseDateInput(value)
  if (Number.isNaN(date.getTime())) return SENTINEL

  // A bare calendar day has no clock reading to show, so it is not rendered as midnight.
  const timeZone = options.timeZone ?? getRuntimeZone()
  const hour12 = options.hour12 ?? false
  const dtfOptions: Intl.DateTimeFormatOptions = {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12,
  }
  const key = `${safeLocale(locale)}|${timeZone}|time|${hour12 ? 1 : 0}`
  return getDtf(locale, dtfOptions, key).format(date)
}

/** Convenience: {@link formatDate} with the time appended (`withTime: true`). */
export function formatDateTime(
  value: DateInput,
  locale: string,
  options: FormatDateOptions = {},
): string {
  return formatDate(value, locale, { ...options, withTime: true })
}

/**
 * Locale-aware compact calendar range used by read-only summaries.
 *
 * Same-day ranges collapse to one date. Within one year the start omits the repeated year; across
 * years both ends stay complete. This preserves the legacy Data Export summary shape while routing
 * every locale decision through the shared formatter.
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  locale: string,
  options: FormatDateRangeOptions = {},
): string {
  const parsedStart = parseRangeEnd(start)
  const parsedEnd = parseRangeEnd(end)
  if (!parsedStart && !parsedEnd) return SENTINEL
  const singleOptions: FormatDateOptions =
    options.timeZone === undefined ? {} : { timeZone: options.timeZone }
  if (!parsedStart) return formatDate(end, locale, singleOptions)
  if (!parsedEnd) return formatDate(start, locale, singleOptions)

  const timeZone =
    options.timeZone ?? (parsedStart.dateOnly && parsedEnd.dateOnly ? 'UTC' : getRuntimeZone())
  const startFields = calendarFields(parsedStart.date, timeZone)
  const endFields = calendarFields(parsedEnd.date, timeZone)
  if (!startFields || !endFields) return SENTINEL

  if (
    startFields.year === endFields.year &&
    startFields.month === endFields.month &&
    startFields.day === endFields.day
  ) {
    return formatRangeEnd(parsedStart.date, locale, timeZone, true)
  }

  const sameYear = startFields.year === endFields.year
  const first = formatRangeEnd(parsedStart.date, locale, timeZone, !sameYear)
  const last = formatRangeEnd(parsedEnd.date, locale, timeZone, true)
  return `${first}${options.separator ?? ' - '}${last}`
}

function parseRangeEnd(value: DateInput): ParsedDate | null {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null
  const parsed = parseDateInput(value)
  return Number.isNaN(parsed.date.getTime()) ? null : parsed
}

function calendarFields(
  date: Date,
  timeZone: string,
): Pick<ZonedDateTimeParts, 'year' | 'month' | 'day'> | null {
  const parts = getZonedDateTimeParts(date, timeZone)
  return parts ? { year: parts.year, month: parts.month, day: parts.day } : null
}

function formatRangeEnd(date: Date, locale: string, timeZone: string, withYear: boolean): string {
  const tag = safeLocale(locale)
  const shape = withYear ? 'range-full' : 'range-short'
  const dateOptions: Intl.DateTimeFormatOptions = {
    timeZone,
    day: '2-digit',
    month: 'short',
  }
  if (withYear) dateOptions.year = 'numeric'
  return getDtf(tag, dateOptions, `${tag}|${timeZone}|${shape}`).format(date)
}

/**
 * Chart-axis bucket label — the locale-correct pure date math from the legacy `dateRangeFormat`, with
 * NO hardcoded locale and NO HTML (the `<br/>` and `W{isoWeek}` glyphs are presentational → module 08).
 * All rendering is in UTC (buckets are calendar spans). Returns `'-'` for nullish/unparseable input.
 *   • `year`          → the 4-digit year (`"2026"`).
 *   • `month_of_year` → localised short month + 2-digit year (`"Jul 26"` / `"Tem 26"`).
 *   • `week`          → localised start-of-week day + short month, plus a 2-digit year when the week
 *                       spans a year boundary (legacy year-span math, L631).
 *   • `day` (default) → localised day + short month.
 */
export function formatBucketLabel(
  value: DateInput,
  granularity: DateBucketGranularity,
  locale: string,
): string {
  if (value == null) return SENTINEL
  if (typeof value === 'string' && value.trim() === '') return SENTINEL

  const { date } = parseDateInput(value)
  if (Number.isNaN(date.getTime())) return SENTINEL

  const tag = safeLocale(locale)

  switch (granularity) {
    case 'year':
      return String(date.getUTCFullYear())

    case 'month_of_year':
      return getDtf(
        tag,
        { timeZone: 'UTC', month: 'short', year: '2-digit' },
        `${tag}|bucket-monthYear`,
      ).format(date)

    case 'week': {
      const end = new Date(date.getTime())
      end.setUTCDate(end.getUTCDate() + 6)
      const startStr = getDtf(
        tag,
        { timeZone: 'UTC', day: 'numeric', month: 'short' },
        `${tag}|bucket-dayMonth`,
      ).format(date)

      if (date.getUTCFullYear() !== end.getUTCFullYear()) {
        const yearStr = getDtf(
          tag,
          { timeZone: 'UTC', year: '2-digit' },
          `${tag}|bucket-year2`,
        ).format(date)
        return `${startStr} ${yearStr}`
      }
      return startStr
    }

    // 'day' and any unrecognised granularity → localised day + short month.
    default:
      return getDtf(
        tag,
        { timeZone: 'UTC', day: 'numeric', month: 'short' },
        `${tag}|bucket-dayMonth`,
      ).format(date)
  }
}
